import React, { useRef, useState } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function PublicTitheSubmission() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    chinese_name: '',
    worship_code: '',
    contact_phone: '',
    email: '',
    receiving_bank: '',
    transaction_type: '',
    deposit_date: '',
    tithe_amount: '',
    thanksgiving_offering: '',
    church_operating: '',
    building_fund: '',
    gospel_ministry: '',
    love_neighbor_fund: '',
    special_offering: '',
    special_offering_description: '',
    receipt_required: true,
    receipt_frequency: 'per_submission',
    remarks: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const banks = [
    '滙豐銀行 (HSBC)',
    '建設銀行 (CCB)'
  ];

  const transactionTypes = [
    '網上轉帳 (Transfer)',
    'ATM (Cash)'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setReceiptFile(null);
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('titheSubmission.errors.invalidFileType'));
      e.target.value = '';
      setReceiptFile(null);
      return;
    }

    setError('');
    setReceiptFile(file);
  };

  const calculateTotal = () => {
    const amounts = [
      parseFloat(formData.tithe_amount) || 0,
      parseFloat(formData.thanksgiving_offering) || 0,
      parseFloat(formData.church_operating) || 0,
      parseFloat(formData.building_fund) || 0,
      parseFloat(formData.gospel_ministry) || 0,
      parseFloat(formData.love_neighbor_fund) || 0,
      parseFloat(formData.special_offering) || 0
    ];
    return amounts.reduce((sum, amount) => sum + amount, 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Validate required fields
      if (!formData.chinese_name.trim()) throw new Error(t('titheSubmission.errors.nameRequired'));
      if (!formData.contact_phone.trim()) throw new Error(t('titheSubmission.errors.phoneRequired'));
      if (!formData.receiving_bank) throw new Error(t('titheSubmission.errors.bankRequired'));
      if (!formData.transaction_type) throw new Error(t('titheSubmission.errors.transactionTypeRequired'));
      if (!formData.deposit_date) throw new Error(t('titheSubmission.errors.depositDateRequired'));
      
      const total = parseFloat(calculateTotal());
      if (total <= 0) throw new Error(t('titheSubmission.errors.amountRequired'));

      const combinedRemarks = [
        formData.remarks || null,
        formData.receipt_frequency
          ? t('titheSubmission.fields.receiptPreferenceNote', {
              pref:
                formData.receipt_frequency === 'yearly'
                  ? t('titheSubmission.fields.receiptFrequencyYearly')
                  : t('titheSubmission.fields.receiptFrequencyPerSubmission')
            })
          : null
      ]
        .filter(Boolean)
        .join('\n');

      // Prepare payload
      const payload = {
        chinese_name: formData.chinese_name,
        worship_code: formData.worship_code || null,
        contact_phone: formData.contact_phone,
        email: formData.email || null,
        receiving_bank: formData.receiving_bank,
        transaction_type: formData.transaction_type,
        deposit_date: formData.deposit_date,
        tithe_amount: parseFloat(formData.tithe_amount) || 0,
        thanksgiving_offering: parseFloat(formData.thanksgiving_offering) || 0,
        church_operating: parseFloat(formData.church_operating) || 0,
        building_fund: parseFloat(formData.building_fund) || 0,
        gospel_ministry: parseFloat(formData.gospel_ministry) || 0,
        love_neighbor_fund: parseFloat(formData.love_neighbor_fund) || 0,
        special_offering: parseFloat(formData.special_offering) || 0,
        special_offering_description: formData.special_offering_description || null,
        total_amount: total,
        receipt_required: formData.receipt_required,
        remarks: combinedRemarks || null
      };

      // Submit to API
      const response = await api.post('/api/v1/tithe-receipts/', payload);
      const receiptId = response?.data?.id;

      if (receiptFile) {
        if (!receiptId) {
          throw new Error(t('titheSubmission.errors.uploadFailed'));
        }

        const uploadData = new FormData();
        uploadData.append('file', receiptFile);

        await api.post(`/api/v1/tithe-receipts/${receiptId}/upload-receipt`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        chinese_name: '',
        worship_code: '',
        contact_phone: '',
        email: '',
        receiving_bank: '',
        transaction_type: '',
        deposit_date: '',
        tithe_amount: '',
        thanksgiving_offering: '',
        church_operating: '',
        building_fund: '',
        gospel_ministry: '',
        love_neighbor_fund: '',
        special_offering: '',
        special_offering_description: '',
        receipt_required: true,
        receipt_frequency: 'per_submission',
        remarks: ''
      });
      setReceiptFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message || t('titheSubmission.errors.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('titheSubmission.title')}
        </h1>
        <p className="text-gray-600 mb-6">
          {t('titheSubmission.subtitle')}
        </p>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">
              {t('titheSubmission.successMessage')}
            </p>
          </div>
        )}

        {!success && error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!success && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('titheSubmission.personalInfo')}
            </h2>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.chineseName')} *
                </label>
                <input
                  type="text"
                  name="chinese_name"
                  value={formData.chinese_name}
                  onChange={handleChange}
                  placeholder={t('titheSubmission.fields.chineseName')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.worshipCode')}
                </label>
                <input
                  type="text"
                  name="worship_code"
                  value={formData.worship_code}
                  onChange={handleChange}
                  placeholder={t('titheSubmission.fields.worshipCode')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {t('titheSubmission.fields.worshipCodeOptional')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('titheSubmission.fields.contactPhone')} *
                  </label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder={t('titheSubmission.fields.contactPhone')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('titheSubmission.fields.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('titheSubmission.fields.email')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('titheSubmission.paymentInfo')}
            </h2>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('titheSubmission.fields.receivingBank')} *
                  </label>
                  <select
                    name="receiving_bank"
                    value={formData.receiving_bank}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('titheSubmission.selectBank')}</option>
                    {banks.map(bank => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('titheSubmission.fields.transactionType')} *
                  </label>
                  <select
                    name="transaction_type"
                    value={formData.transaction_type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('titheSubmission.selectTransactionType')}</option>
                    {transactionTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.depositDate')} *
                </label>
                <input
                  type="datetime-local"
                  name="deposit_date"
                  value={formData.deposit_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Donation Amounts Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('titheSubmission.donationAmounts')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.tithe')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="tithe_amount"
                    value={formData.tithe_amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.thanksgiving')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="thanksgiving_offering"
                    value={formData.thanksgiving_offering}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.churchOperating')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="church_operating"
                    value={formData.church_operating}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.buildingFund')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="building_fund"
                    value={formData.building_fund}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.gospelMinistry')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="gospel_ministry"
                    value={formData.gospel_ministry}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.loveNeighbor')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="love_neighbor_fund"
                    value={formData.love_neighbor_fund}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.specialOffering')}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-2 text-gray-500">HK$</span>
                  <input
                    type="number"
                    name="special_offering"
                    value={formData.special_offering}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.specialOfferingDesc')}
                </label>
                <input
                  type="text"
                  name="special_offering_description"
                  value={formData.special_offering_description}
                  onChange={handleChange}
                  placeholder={t('titheSubmission.fields.specialOfferingDesc')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Total Amount Display */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">
                  {t('titheSubmission.totalAmount')}
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  HK$ {total}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('titheSubmission.additionalInfo')}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.uploadReceipt')}
                </label>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  className="w-full text-sm text-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('titheSubmission.fields.uploadHint')}
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="receipt_required"
                    checked={formData.receipt_required}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t('titheSubmission.fields.receiptRequired')}
                  </span>
                </label>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t('titheSubmission.fields.receiptFrequency')}
                </p>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="receipt_frequency"
                      value="per_submission"
                      checked={formData.receipt_frequency === 'per_submission'}
                      onChange={handleChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{t('titheSubmission.fields.receiptFrequencyPerSubmission')}</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="receipt_frequency"
                      value="yearly"
                      checked={formData.receipt_frequency === 'yearly'}
                      onChange={handleChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span>{t('titheSubmission.fields.receiptFrequencyYearly')}</span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t('titheSubmission.fields.receiptFrequencyHint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('titheSubmission.fields.remarks')}
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder={t('titheSubmission.fields.remarksPlaceholder')}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? t('titheSubmission.submitting') : t('titheSubmission.submit')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
